/**
 * Origin verification helpers used by both ends of the popup transport.
 * Spec §5.3.
 */

/**
 * Schemes that we accept for origins. Production uses https only; we allow
 * http here so test setups and `localhost:port` work — callers can layer
 * tighter rules (e.g. "only https in production") on top.
 */
export const ALLOWED_ORIGIN_SCHEMES: readonly string[] = ['https:', 'http:'];

/**
 * Strict origin equality check. Returns true iff both inputs are non-empty,
 * URL-parseable, and have matching `origin` (scheme + host + port).
 */
export function isMatchingOrigin(a: string | undefined, b: string | undefined): boolean {
  if (!a || !b) return false;
  try {
    const ua = new URL(a);
    const ub = new URL(b);
    return ua.origin === ub.origin;
  } catch {
    return false;
  }
}

/**
 * Source binding: does this message come from the window this transport owns?
 *
 * This is NOT an authentication boundary — `event.origin` already is one, and
 * the browser sets it from the sending document, so it cannot be forged. This
 * check disambiguates OUR OWN same-origin surfaces from each other, which the
 * origin check cannot do because they share an origin by construction.
 *
 * It matters because a host window can have several wallet surfaces alive at
 * once: `register()`, and the login / cold-sign popup fallbacks taken when the
 * framed ceremony is refused by its effective Permissions Policy, each open a
 * `PopupTransport` while the `IframeTransport` is still listening on the same
 * `hostWindow`. Without this, the popup's `wallet:ready` flips the iframe
 * transport's ready latch and flushes its outbox into an iframe that has not
 * loaded (messages lost, request times out), and the popup's
 * out-of-band messages are attributed to the wrong surface entirely.
 *
 * The original example was `wallet:session:keyed`: a popup-sourced ack was
 * accepted as if the IFRAME had adopted the session, so `_hasKeys` went `true`
 * while the iframe held no keys and the next sign went to a surface that could
 * not perform it. S1 PR2 deleted that message, and the check is no less
 * load-bearing for it — `wallet:diag` is out-of-band on the same terms, and
 * every message correlated by request id still depends on this binding to
 * decide whose reply it is.
 *
 * An absent `expected` (no window yet, or already closed) and an absent
 * `event.source` are both treated as unverifiable and allowed through:
 * synthetic `MessageEvent`s carry no source, and a message that reached here at
 * all already passed the origin check. Only a positive mismatch is rejected.
 */
export function isFromExpectedSource(source: unknown, expected: unknown): boolean {
  if (!source || !expected) return true;
  return source === expected;
}
